/**
 * Gear Rack predictive search v5
 *
 * The untouched query is always tried first. Its returned products are then
 * filtered with the same strict/prefix rules as the full search page. Only if
 * that produces zero relevant products do we try an autocorrection.
 */
if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    constructor() {
      super();
      this.activeSearchTerm = '';
      this.correctedFrom = '';
      this.requestSerial = 0;
    }

    connectedCallback() {
      this.is_drawer = this.dataset.drawer;
      this.drawer = this.closest('#Search-Drawer');
      this.form = this.querySelector('form');
      this.button = document.querySelectorAll('.thb-quick-search');
      this.input = this.querySelector('.search-field');
      this.defaultTab = this.querySelector('.side-panel-content--initial');
      this.predictiveSearchResults = this.querySelector('.thb-predictive-search');
      this.cc = this.querySelector('.searchform--click-capture');

      if (!this.form || !this.input || !this.predictiveSearchResults) return;
      this.setupEventListeners();
    }

    setupEventListeners() {
      this.input.addEventListener('input', debounce(() => this.onChange(), 180));
      this.input.addEventListener('focus', debounce(() => this.onChange(), 120));

      this.button.forEach((item) => {
        item.addEventListener('click', (event) => {
          event.preventDefault();
          document.body.classList.add('open-cc');
          if (this.is_drawer) this.drawer?.classList.add('active');
          return false;
        });
      });

      this.cc?.addEventListener('click', this.close.bind(this));
      document.addEventListener('keyup', (event) => {
        if (event.code && event.code.toUpperCase() === 'ESCAPE') this.close();
      });
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      const originalTerm = this.getQuery();
      if (!originalTerm.length) {
        this.predictiveSearchResults.innerHTML = '';
        if (!this.is_drawer) this.close();
        return;
      }

      if (!this.is_drawer) this.open();
      this.predictiveSearchResults.classList.add('active');
      this.getSearchResults(originalTerm);
    }

    predictiveUrl(searchTerm) {
      const fields = 'title,product_type,variants.title,vendor,tag,variants.sku';
      return `${theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}` +
        `&${encodeURIComponent('resources[type]')}=product` +
        `&${encodeURIComponent('resources[limit]')}=10` +
        `&${encodeURIComponent('resources[limit_scope]')}=each` +
        `&${encodeURIComponent('resources[options][fields]')}=${encodeURIComponent(fields)}` +
        `&${encodeURIComponent('resources[options][unavailable_products]')}=last` +
        '&section_id=predictive-search';
    }

    async fetchResultsMarkup(searchTerm) {
      const response = await fetch(this.predictiveUrl(searchTerm), { credentials: 'same-origin' });
      const text = await response.text();
      if (!response.ok) throw new Error(`${response.status}: ${text}`);
      const section = new DOMParser()
        .parseFromString(text, 'text/html')
        .querySelector('#shopify-section-predictive-search');
      return section ? section.innerHTML : '';
    }

    cardData(card) {
      return {
        title: card.dataset.searchTitle || card.querySelector('.product-card-small--link')?.textContent || '',
        type: card.dataset.searchType || '',
        vendor: card.dataset.searchVendor || '',
        tags: card.dataset.searchTags || '',
        text: card.dataset.searchText || '',
        variants: card.dataset.searchVariants || '',
        sku: card.dataset.searchSku || '',
        handle: card.dataset.productHandle || ''
      };
    }

    filterMarkup(resultsMarkup, query) {
      if (!resultsMarkup) return { markup: '', count: 0 };
      const wrapper = document.createElement('div');
      wrapper.innerHTML = resultsMarkup;
      const assist = window.GearRackSearchAssist;
      let count = 0;

      wrapper.querySelectorAll('product-card-small.product-card-small').forEach((card) => {
        const matches = assist ? assist.productMatchesQuery(query, this.cardData(card)) : true;
        if (!matches) {
          card.remove();
        } else {
          count += 1;
        }
      });

      if (count === 0) return { markup: '', count: 0 };
      return { markup: wrapper.innerHTML, count };
    }

    async getSearchResults(originalTerm) {
      const serial = ++this.requestSerial;
      this.predictiveSearchResults.classList.add('loading');

      try {
        // 1. Preserve the customer's exact input first. This is what makes one
        // character and unfinished-word searches work instead of being "fixed".
        const originalMarkup = await this.fetchResultsMarkup(originalTerm);
        if (serial !== this.requestSerial || originalTerm !== this.getQuery()) return;

        const originalFiltered = this.filterMarkup(originalMarkup, originalTerm);
        if (originalFiltered.count > 0) {
          this.activeSearchTerm = originalTerm;
          this.correctedFrom = '';
          this.renderSearchResults(originalFiltered.markup);
          return;
        }

        // 2. No strict/prefix products matched the untouched query. Now, and only
        // now, allow the correction engine to repair a high-confidence typo.
        const assist = window.GearRackSearchAssist;
        const corrected = assist
          ? await assist.resolveCorrection(originalTerm, { skipStrictLookup: true })
          : originalTerm;
        if (serial !== this.requestSerial || originalTerm !== this.getQuery()) return;

        const normalize = assist?.normalize || ((value) => String(value || '').toLowerCase().trim());
        const changed = normalize(corrected) !== normalize(originalTerm);

        if (!changed) {
          this.activeSearchTerm = originalTerm;
          this.correctedFrom = '';
          this.renderNoResults(originalTerm);
          return;
        }

        const correctedMarkup = await this.fetchResultsMarkup(corrected);
        if (serial !== this.requestSerial || originalTerm !== this.getQuery()) return;
        const correctedFiltered = this.filterMarkup(correctedMarkup, corrected);

        if (correctedFiltered.count > 0) {
          this.activeSearchTerm = corrected;
          this.correctedFrom = originalTerm;
          this.renderSearchResults(correctedFiltered.markup);
        } else {
          this.activeSearchTerm = originalTerm;
          this.correctedFrom = '';
          this.renderNoResults(originalTerm);
        }
      } catch (error) {
        console.warn('Predictive search could not finish:', error);
      } finally {
        if (serial === this.requestSerial) this.predictiveSearchResults.classList.remove('loading');
      }
    }

    renderNoResults(query) {
      const safe = document.createElement('div');
      safe.className = 'gr-predictive-no-results';
      safe.textContent = `No products found for “${query}”.`;
      this.predictiveSearchResults.replaceChildren(safe);
    }

    renderSearchResults(resultsMarkup) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;

      if (this.correctedFrom && this.activeSearchTerm) {
        const notice = document.createElement('div');
        notice.className = 'gr-search-correction-notice';
        notice.append(document.createTextNode('Showing results for '));
        const strong = document.createElement('strong');
        strong.textContent = this.activeSearchTerm;
        notice.append(strong, document.createTextNode(` instead of ${this.correctedFrom}.`));
        this.predictiveSearchResults.prepend(notice);
      }

      const submit = this.predictiveSearchResults.querySelector('#search-results-submit');
      if (!submit) return;
      if (!this.is_drawer) submit.classList.remove('button');
      submit.addEventListener('click', () => {
        const assist = window.GearRackSearchAssist;
        const used = this.activeSearchTerm || this.getQuery();
        const original = this.correctedFrom || '';
        window.location.assign(
          assist
            ? assist.searchUrl(used, original).toString()
            : `${theme.routes.search_url}?q=${encodeURIComponent(used)}&type=product&options%5Bprefix%5D=last`
        );
      });
    }

    close() {
      this.predictiveSearchResults.setAttribute('inert', '');
      document.body.classList.remove('open-search');
    }

    open() {
      document.body.classList.add('open-search');
      this.predictiveSearchResults.removeAttribute('inert');
    }
  }

  customElements.define('search-form', SearchForm);
}
